import { useEffect, useMemo, useState } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useCreateOrderMutation } from '~entities/order';
import type { Pos } from '~entities/pos';
import { isOnlinePos, useGetPointsOfSalesQuery } from '~entities/pos';
import { isProduct, useFindProductByParams } from '~entities/product';

import { combineValidators } from '~shared/lib/combine-validators';
import type { AbstractOption } from '~shared/types/abstract-options';
import { validators } from '~shared/validators';

import Popup from '@platform-ui/popup';

import { LoadingButton } from '@breeze-platform-ui/button';
import Button from '@breeze-platform-ui/button';
import { Int24Plus } from '@breeze-platform-ui/iconsPack';
import { NotificationInline } from '@breeze-platform-ui/notification';
import { Input, InputPhone, Select } from '@breeze/rhf-adapters';

import { HelpBanner } from './help-banner/help-banner';
import { ItemForm } from './item-form/item-form';
import { OrderItems } from './order-items/order-items';
import { OrderLink } from './order-link/order-link';

import { convertFormValuesToOrder } from '../lib/convert-form-values-to-order';
import { getTotalPrice } from '../lib/get-total-price';
import { validateLoanAmount } from '../lib/loan-validator';

export type ItemFormValue = {
  category: string;
  name: string;
  price: string;
  quantity: number | string;
  isUsed?: boolean;
};

export type OrderFormValues = {
  posId: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  orderNumber?: string;
  items: ItemFormValue[];
};

export const OrderForm = () => {
  const methods = useForm<OrderFormValues>({
    mode: 'all',
  });
  const { control, handleSubmit, setValue, watch, setError, clearErrors } =
    methods;
  const fieldArrayMethods = useFieldArray({
    control,
    name: 'items',
  });
  const { fields, append } = fieldArrayMethods;
  const [itemPopupOpened, setItemPopupOpened] = useState<boolean>(false);
  const [linkPopupOpened, setLinkPopupOpened] = useState<boolean>(false);
  const navigate = useNavigate();

  const {
    data: posList = [],
    isSuccess,
    isLoading: isPosListLoading,
  } = useGetPointsOfSalesQuery();

  const onlinePosList = useMemo(() => posList.filter(isOnlinePos), [posList]);
  const onlinePosListMap = useMemo(
    () =>
      onlinePosList.reduce(
        (result, pos) => {
          result[pos.id] = pos;
          return result;
        },
        {} as Record<string, Pos>
      ),
    [onlinePosList]
  );
  const posListOptions: AbstractOption[] = onlinePosList.map((pos) => ({
    value: pos.id,
    title: pos.name,
  }));

  useEffect(() => {
    if (isSuccess && onlinePosList.length === 1) {
      setValue('posId', onlinePosList[0].id);
    }
  }, [onlinePosList, isSuccess, setValue]);

  const posId = watch('posId');
  const merchantId = onlinePosListMap[posId]?.merchantId;
  const orderItems = watch('items')?.map((field) => ({
    ...field,
    price: +field.price,
    quantity: +field.quantity,
  }));

  const { data: findProductResult, isFetching: isProductLoading } =
    useFindProductByParams(
      {
        pointOfSaleId: posId,
        merchantId,
        orderItems,
      },
      { enabled: !!posId && !!merchantId && orderItems?.length > 0 }
    );

  const isProductFound = isProduct(findProductResult);

  const {
    mutate,
    isLoading: isOrderCreating,
    isSuccess: isOrderCreated,
    data: createOrderResponse,
    error: createOrderError,
  } = useCreateOrderMutation();

  const submitForm = (values: OrderFormValues) => {
    if (!isProductFound) {
      return;
    }

    clearErrors('orderNumber');

    mutate(convertFormValuesToOrder(values, findProductResult.productId));
  };

  useEffect(() => {
    if (
      createOrderError &&
      typeof createOrderError === 'object' &&
      'code' in createOrderError
    ) {
      if (createOrderError.code === 'ORDER_NUMBER_ALREADY_EXISTS') {
        setError('orderNumber', {
          type: 'manual',
          message:
            'This order number already exists. Please enter a unique value',
        });
      }
    }
  }, [createOrderError, setError]);

  useEffect(() => {
    if (isOrderCreated && createOrderResponse.orderFormUrl) {
      setLinkPopupOpened(true);
    }
  }, [isOrderCreated, createOrderResponse]);

  const totalPrice = useMemo(() => getTotalPrice(fields), [fields]);
  const totalPriceError =
    totalPrice > 0 &&
    findProductResult &&
    validateLoanAmount(totalPrice, findProductResult);

  const isCreateOrderDisabled =
    !fields.length || !isProductFound || isOrderCreating || !!totalPriceError;

  return (
    <>
      <div className="box-border flex w-full gap-[70px] px-9 py-11">
        <form
          onSubmit={handleSubmit(submitForm)}
          className="min-w-[434px] flex-grow"
        >
          <Controller
            name="posId"
            control={control}
            rules={{ required: 'Please, select a store' }}
            render={(props) => (
              <Select
                {...props}
                label="Store name"
                options={posListOptions}
                readOnly={onlinePosList.length === 1}
                loading={isPosListLoading}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            rules={{
              validate: combineValidators(
                validators.phoneLength(),
                validators.mobilePhoneFormat()
              ),
            }}
            render={(props) => (
              <InputPhone {...props} label="Customer’s mobile number" />
            )}
          />
          <Controller
            name="firstName"
            control={control}
            rules={{
              validate: combineValidators(
                validators.maxLength({ maxLength: 300 }),
                validators.filipinoName()
              ),
            }}
            render={(props) => (
              <Input label="Customer’s first name" {...props} />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            rules={{
              validate: combineValidators(
                validators.maxLength({ maxLength: 300 }),
                validators.filipinoName()
              ),
            }}
            render={(props) => (
              <Input label="Customer’s last name" {...props} />
            )}
          />
          <Controller
            name="email"
            control={control}
            rules={{
              validate: validators.email({
                invalidSymbolsText: 'Invalid characters',
                invalidFormatText: 'The format must be name@email.com',
              }),
            }}
            render={(props) => <Input label="Customer’s email" {...props} />}
          />
          <Controller
            name="orderNumber"
            control={control}
            render={(props) => <Input label="Order number" {...props} />}
          />
          <div className="mb-5 mt-7">
            <h4 className="mb-3 font-heading text-lg font-bold">
              Items to buy
            </h4>
            <OrderItems {...fieldArrayMethods} />
            {!!totalPriceError && (
              <div className="mb-5">
                <NotificationInline
                  showClose={false}
                  type="warning"
                  withIcon={false}
                  timer={false}
                  animateFirstRender={false}
                >
                  {totalPriceError}
                </NotificationInline>
              </div>
            )}

            <Button
              wide
              theme="secondary"
              icon={<Int24Plus />}
              onClick={() => {
                setItemPopupOpened(true);
              }}
            >
              Add an item
            </Button>
          </div>
          <LoadingButton
            type="submit"
            wide
            disabled={isCreateOrderDisabled}
            loading={isOrderCreating || isProductLoading}
          >
            Create order
          </LoadingButton>
        </form>
        <HelpBanner />
      </div>
      <Popup
        closeable
        size="s"
        opened={itemPopupOpened}
        onClose={() => setItemPopupOpened(false)}
      >
        <FormProvider {...methods}>
          <ItemForm
            onDone={(item) => {
              append(item);
              setItemPopupOpened(false);
            }}
            allowsUsedItems={onlinePosListMap[posId]?.allowsUsedItems}
          />
        </FormProvider>
      </Popup>
      {createOrderResponse && (
        <Popup
          closeable
          size="auto"
          opened={linkPopupOpened}
          onClose={() => navigate('/orders')}
        >
          <OrderLink link={createOrderResponse.orderFormUrl} />
        </Popup>
      )}
    </>
  );
};
